import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, ArrowRight, RotateCw, Globe } from 'lucide-react';

interface IframeNavBarProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  isRecording: boolean;
  addressBarUrl: string;
  setAddressBarUrl: (url: string) => void;
  onNavigate: (url: string) => void;
}

/**
 * Barra de navegação flutuante para o iframe durante gravação.
 * - F8 toggle visibilidade (animação slide-down)
 * - Mouse hover mantém visível
 * - 2s sem mouse → oculta
 * - Só aparece durante gravação
 */
export function IframeNavBar({ iframeRef, isRecording, addressBarUrl, setAddressBarUrl, onNavigate }: IframeNavBarProps) {
  const [visible, setVisible] = useState(false);
  const [localUrl, setLocalUrl] = useState(addressBarUrl);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringRef = useRef(false);
  const isFocusedRef = useRef(false);
  const barRef = useRef<HTMLDivElement>(null);

  // Sync localUrl when addressBarUrl changes externally (but NOT while user is typing)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalUrl(addressBarUrl);
    }
  }, [addressBarUrl]);

  // Track iframe URL changes (polling every 500ms) — paused while input is focused
  useEffect(() => {
    if (!isRecording) return;
    const interval = setInterval(() => {
      if (isFocusedRef.current) return; // User is typing — don't touch
      try {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow?.location?.href) {
          const currentUrl = iframe.contentWindow.location.href;
          if (currentUrl && currentUrl !== 'about:blank' && currentUrl !== localUrl) {
            setLocalUrl(currentUrl);
            setAddressBarUrl(currentUrl);
          }
        }
      } catch {
        // Cross-origin — can't read, ignore
      }
    }, 500);
    return () => clearInterval(interval);
  }, [isRecording, iframeRef, localUrl, setAddressBarUrl]);

  // F8 key handler — only during recording
  useEffect(() => {
    if (!isRecording) {
      setVisible(false);
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F8') {
        e.preventDefault();
        e.stopPropagation();
        setVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Also listen on iframe doc
    const attachIframeListener = () => {
      try {
        const doc = iframeRef.current?.contentDocument;
        if (doc) {
          doc.addEventListener('keydown', handleKeyDown);
        }
      } catch {}
    };
    attachIframeListener();
    const interval = setInterval(attachIframeListener, 2000);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearInterval(interval);
      try {
        const doc = iframeRef.current?.contentDocument;
        if (doc) doc.removeEventListener('keydown', handleKeyDown);
      } catch {}
    };
  }, [isRecording, iframeRef]);

  // Auto-hide after 2s when mouse leaves
  const startHideTimer = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        setVisible(false);
      }
    }, 2000);
  }, []);

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
    startHideTimer();
  }, [startHideTimer]);

  // Start hide timer when bar becomes visible
  useEffect(() => {
    if (visible && !isHoveringRef.current) {
      startHideTimer();
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [visible, startHideTimer]);

  // Navigation handlers
  const goBack = useCallback(() => {
    try {
      iframeRef.current?.contentWindow?.history.back();
    } catch {}
  }, [iframeRef]);

  const goForward = useCallback(() => {
    try {
      iframeRef.current?.contentWindow?.history.forward();
    } catch {}
  }, [iframeRef]);

  const refresh = useCallback(() => {
    try {
      iframeRef.current?.contentWindow?.location.reload();
    } catch {}
  }, [iframeRef]);

  const navigate = useCallback(() => {
    if (localUrl.trim()) {
      let url = localUrl.trim();
      // Auto-add protocol if missing
      if (!url.startsWith('http') && !url.startsWith('/')) {
        url = '/' + url;
      }
      onNavigate(url);
    }
  }, [localUrl, onNavigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      navigate();
    }
    // Stop propagation to prevent recording shortcuts
    e.stopPropagation();
  }, [navigate]);

  if (!isRecording) return null;

  return (
    <div
      ref={barRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: visible
          ? 'translateX(-50%) translateY(0)'
          : 'translateX(-50%) translateY(-100%)',
        zIndex: 99999,
        transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s ease',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        // Bar design
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '5px 8px',
        background: 'rgba(15, 23, 42, 0.92)',
        backdropFilter: 'blur(12px)',
        borderRadius: '0 0 12px 12px',
        border: '1px solid rgba(56, 189, 248, 0.2)',
        borderTop: 'none',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset',
        minWidth: 420,
        maxWidth: '70vw',
      }}
    >
      {/* Back */}
      <button
        onClick={goBack}
        title="Voltar (←)"
        style={navBtnStyle}
      >
        <ArrowLeft size={14} />
      </button>

      {/* Forward */}
      <button
        onClick={goForward}
        title="Avançar (→)"
        style={navBtnStyle}
      >
        <ArrowRight size={14} />
      </button>

      {/* Refresh */}
      <button
        onClick={refresh}
        title="Recarregar"
        style={navBtnStyle}
      >
        <RotateCw size={13} />
      </button>

      {/* Address bar */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(30, 41, 59, 0.8)',
        borderRadius: 6,
        border: '1px solid rgba(71, 85, 105, 0.5)',
        padding: '0 8px',
        minWidth: 0,
      }}>
        <Globe size={12} style={{ color: '#64748b', flexShrink: 0 }} />
        <input
          type="text"
          value={localUrl}
          onChange={(e) => setLocalUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => { isFocusedRef.current = true; e.target.select(); }}
          onBlur={() => { isFocusedRef.current = false; }}
          placeholder="Digite a URL..."
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            color: '#e2e8f0',
            fontSize: '.72rem',
            fontFamily: 'monospace',
            padding: '5px 0',
            outline: 'none',
            minWidth: 0,
          }}
        />
      </div>

      {/* Go button */}
      <button
        onClick={navigate}
        title="Ir (Enter)"
        style={{
          ...navBtnStyle,
          background: 'rgba(34, 197, 94, 0.15)',
          color: '#22c55e',
          fontWeight: 800,
          fontSize: '.6rem',
          padding: '4px 10px',
          letterSpacing: '.5px',
        }}
      >
        IR
      </button>

      {/* F8 hint */}
      <span style={{
        fontSize: '.5rem',
        color: '#475569',
        letterSpacing: '.5px',
        whiteSpace: 'nowrap',
        marginLeft: 2,
      }}>
        F8
      </span>
    </div>
  );
}

const navBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 6,
  border: '1px solid rgba(71, 85, 105, 0.4)',
  background: 'rgba(30, 41, 59, 0.6)',
  color: '#94a3b8',
  cursor: 'pointer',
  transition: 'all 0.15s',
  flexShrink: 0,
  padding: 0,
};
